import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, CalendarDays, FileText, CheckCircle, XCircle, Tv } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function Dashboard() {
  const [dados, setDados] = useState(null);
  const [relatorio, setRelatorio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get('/api/dashboard'),
      axios.get('/api/relatorios/geral')
    ]).then(([dRes, rRes]) => {
      setDados(dRes.data);
      setRelatorio(rRes.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const dadosPizza = [
    { name: 'Aprovados', value: dados?.pautasAprovadas || 0, color: '#16a34a' },
    { name: 'Reprovados', value: dados?.pautasReprovadas || 0, color: '#dc2626' },
    { name: 'Pendentes', value: (dados?.totalPautas || 0) - (dados?.pautasAprovadas || 0) - (dados?.pautasReprovadas || 0), color: '#d97706' },
  ].filter(d => d.value > 0);

  const sessoesPorMes = {};
  relatorio?.sessoes?.forEach(s => {
    const mes = format(new Date(s.data), 'MMM/yy', { locale: ptBR });
    if (!sessoesPorMes[mes]) sessoesPorMes[mes] = { mes, sessoes: 0, aprovadas: 0, reprovadas: 0 };
    sessoesPorMes[mes].sessoes++;
    sessoesPorMes[mes].aprovadas += s.pautas?.filter(p => p.status === 'APROVADA').length || 0;
    sessoesPorMes[mes].reprovadas += s.pautas?.filter(p => p.status === 'REPROVADA').length || 0;
  });
  const dadosBarras = Object.values(sessoesPorMes).slice(-6);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Visão geral do sistema</p>
        </div>
        {dados?.sessaoAtiva && (
          <Link to={`/tv/${dados.sessaoAtiva.id}`} target="_blank" className="btn btn-primary">
            <Tv size={15} /> Abrir Painel TV
          </Link>
        )}
      </div>

      {/* Stat cards */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dbeafe' }}>
            <Users size={22} color="#2563eb" />
          </div>
          <div className="stat-info">
            <h3>{dados?.totalVereadores ?? 0}</h3>
            <p>Vereadores Ativos</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e0e7ff' }}>
            <CalendarDays size={22} color="#4338ca" />
          </div>
          <div className="stat-info">
            <h3>{dados?.totalSessoes ?? 0}</h3>
            <p>Sessões Realizadas</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fef9c3' }}>
            <FileText size={22} color="#ca8a04" />
          </div>
          <div className="stat-info">
            <h3>{dados?.totalPautas ?? 0}</h3>
            <p>Pautas Cadastradas</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dcfce7' }}>
            <CheckCircle size={22} color="#16a34a" />
          </div>
          <div className="stat-info">
            <h3>{dados?.pautasAprovadas ?? 0}</h3>
            <p>Projetos Aprovados</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fee2e2' }}>
            <XCircle size={22} color="#dc2626" />
          </div>
          <div className="stat-info">
            <h3>{dados?.pautasReprovadas ?? 0}</h3>
            <p>Projetos Reprovados</p>
          </div>
        </div>
      </div>

      {/* Sessão ativa */}
      {dados?.sessaoAtiva ? (
        <div className="card card-border-danger" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div style={{ marginBottom: 8 }}>
                <div className="em-votacao-pulse">
                  <span className="dot-pulse" /> SESSÃO EM ANDAMENTO
                </div>
              </div>
              <h2 style={{ fontSize: '1.05rem', fontWeight: 700 }}>{dados.sessaoAtiva.titulo}</h2>
              <p style={{ color: 'var(--texto-suave)', fontSize: '0.8rem', marginTop: 3 }}>
                {dados.sessaoAtiva.pautas.length} pauta(s) cadastrada(s)
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link to={`/sessoes/${dados.sessaoAtiva.id}`} className="btn btn-secondary">
                Gerenciar
              </Link>
              <Link to={`/tv/${dados.sessaoAtiva.id}`} target="_blank" className="btn btn-primary">
                <Tv size={14} /> Painel TV
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 20 }}>
          <p style={{ color: 'var(--texto-suave)', textAlign: 'center', padding: '12px 0', fontSize: '0.825rem' }}>
            Nenhuma sessão em andamento no momento.{' '}
            <Link to="/sessoes" style={{ color: 'var(--acento)', fontWeight: 600 }}>Criar sessão</Link>
          </p>
        </div>
      )}

      {/* Gráficos */}
      {dados?.totalPautas > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Resultado das Pautas</span>
            </div>
            {dadosPizza.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={190}>
                  <PieChart>
                    <Pie
                      data={dadosPizza}
                      cx="50%" cy="50%"
                      innerRadius={52} outerRadius={76}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {dadosPizza.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                  {dadosPizza.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 9, height: 9, borderRadius: '50%', background: d.color }} />
                        <span style={{ color: 'var(--texto-suave)' }}>{d.name}</span>
                      </div>
                      <strong>{d.value}</strong>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ textAlign: 'center', color: 'var(--texto-suave)', padding: '36px 0', fontSize: '0.825rem' }}>
                Nenhuma pauta registrada ainda.
              </p>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Votações por Mês</span>
            </div>
            {dadosBarras.length > 0 ? (
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={dadosBarras} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <XAxis dataKey="mes" tick={{ fontSize: 11, fill: 'var(--texto-suave)' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--texto-suave)' }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid var(--cinza)', fontSize: 12 }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(value) => value === 'aprovadas' ? 'Aprovadas' : 'Reprovadas'}
                  />
                  <Bar dataKey="aprovadas" fill="#16a34a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="reprovadas" fill="#dc2626" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ textAlign: 'center', color: 'var(--texto-suave)', padding: '36px 0', fontSize: '0.825rem' }}>
                Nenhuma sessão registrada ainda.
              </p>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
